import React, { useState, useEffect, useCallback } from 'react';
import {
  Paper, Typography, Box, Chip, Button, Stack, Divider, Tooltip, IconButton, CircularProgress
} from '@mui/material';
import InboxIcon from '@mui/icons-material/Inbox';
import DoneIcon from '@mui/icons-material/Done';
import VisibilityIcon from '@mui/icons-material/Visibility';
import api from '../../../services/api';

/**
 * InboxWidget — non-interrupt notices for this therapist.
 *
 * What this is NOT: the crisis surface. Critical alerts keep their own banner
 * (CareNoteAlerts) and their own acknowledgement path. Nothing here can be
 * critical — the server refuses to write such a notice — so this widget can be
 * read calmly and skipped safely, which is precisely what makes it readable.
 *
 * A worklist, not a feed: it shows what still needs attention and drains as
 * items are marked seen or done. If it ever starts looking like a timeline,
 * something has gone wrong with the producers rather than the UI.
 */

const SEVERITY_COLOR = { attention: 'warning', notice: 'info', info: 'default' };

// subject.clientId arrives populated ({ _id, name, email }) from the list
// endpoint, but is a bare id when it has not been populated. Handle both rather
// than silently render nothing, which is what made every row anonymous.
const clientIdOf = (item) => {
  const c = item?.subject?.clientId;
  return c && typeof c === 'object' ? c._id : c;
};
const nameOf = (item) => {
  const c = item?.subject?.clientId;
  return c && typeof c === 'object' ? (c.name || c.email) : null;
};

const InboxWidget = ({ token, onOpenClient, onOpenMoment }) => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);
  const [error, setError] = useState('');
  // The archive. Items marked done leave the working list — that is what makes
  // it a worklist rather than a feed — but they are not destroyed, so there has
  // to be a way back to them. The API already supported this via includeDone;
  // only the UI had no way to ask.
  const [showArchive, setShowArchive] = useState(false);

  const load = useCallback(async () => {
    try {
      // api.get is (url, token) and builds the x-auth-token header from it.
      // setAuthToken also sets that header globally, which is why sibling
      // widgets get away with omitting it — but passing it explicitly is the
      // contract, and it still works if the global is cleared between mount
      // and fetch.
      const res = await api.get(`/therapist/inbox${showArchive ? '?includeDone=true' : ''}`, token);
      setItems((res.data || res)?.items || []);
      setError('');
    } catch (err) {
      // Distinguish "nothing to show" from "could not load". An empty inbox and
      // a failed fetch must never look the same: that is how a broken feed
      // reads as a quiet day (the CareNoteAlerts failure mode).
      setError(err?.message || 'Could not load your inbox');
    } finally {
      setLoading(false);
    }
    // showArchive is read inside, so it must be a dependency: without it the
    // first toggle would refetch using the previous view's URL and show the
    // wrong list. Not a lint nicety — a real staleness bug.
  }, [token, showArchive]);

  useEffect(() => {
    setLoading(true);
    load();
    const t = setInterval(load, 120000);
    return () => clearInterval(t);
  }, [load, showArchive]);

  const move = async (id, next) => {
    setBusyId(id);
    try {
      await api.post(`/therapist/inbox/${id}/${next}`, {}, token);
      // In the working list, done means gone — that is what makes it drain. In
      // the archive it must stay put, otherwise the row you just looked at
      // vanishes from the very view whose job is to keep it.
      setItems(prev => (next === 'done' && !showArchive)
        ? prev.filter(i => i._id !== id)
        : prev.map(i => (i._id === id ? { ...i, state: next } : i)));
    } catch (err) {
      setError(err?.message || 'Could not update that item');
    } finally {
      setBusyId(null);
    }
  };

  const needsAction = items.filter(i => i.state === 'needs_action').length;

  if (loading) {
    return (
      <Paper elevation={0} sx={{ p: 2, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)' }}>
        <Stack direction="row" spacing={1} alignItems="center">
          <InboxIcon color="action" />
          <Typography variant="h6">Inbox</Typography>
          <CircularProgress size={16} sx={{ ml: 1 }} />
        </Stack>
      </Paper>
    );
  }

  return (
    <Paper elevation={0} sx={{ p: 2, borderRadius: '16px', border: '1px solid rgba(0,0,0,0.05)' }}>
      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1 }}>
        <InboxIcon color="action" />
        <Typography variant="h6" sx={{ flexGrow: 1 }}>Inbox</Typography>
        {needsAction > 0 && !showArchive && <Chip size="small" color="primary" label={`${needsAction} new`} />}
        <Button size="small" onClick={() => setShowArchive(v => !v)}>
          {showArchive ? 'Back to inbox' : 'Archive'}
        </Button>
      </Stack>

      {error && (
        <Typography variant="body2" color="error" sx={{ mb: 1 }}>
          {error}
        </Typography>
      )}

      {!error && items.length === 0 && (
        <Typography variant="body2" color="text.secondary">
          {showArchive
            ? 'Nothing archived yet. Items you mark done will appear here.'
            : 'Nothing needs your attention here. Crisis alerts appear at the top of the dashboard, not in this list.'}
        </Typography>
      )}

      <Stack divider={<Divider flexItem />} spacing={1}>
        {items.map(item => (
          <Box key={item._id} sx={{ pt: 1, opacity: item.state === 'seen' ? 0.65 : 1 }}>
            <Stack direction="row" spacing={1} alignItems="flex-start">
              <Box
                sx={{ flexGrow: 1, minWidth: 0, cursor: clientIdOf(item) ? 'pointer' : 'default' }}
                onClick={() => { const c = clientIdOf(item); if (c && onOpenClient) onOpenClient(c); }}
              >
                <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: 'wrap' }}>
                  {/* Who it is about, first and in the strongest weight on the
                      row. A notice that does not say whose it is cannot be
                      triaged at a glance, and the name was being fetched and
                      discarded — the API populates it already. */}
                  {nameOf(item) && (
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      {nameOf(item)}
                    </Typography>
                  )}
                  <Typography variant="subtitle2" sx={{ fontWeight: 400 }}>{item.title}</Typography>
                  <Chip
                    size="small"
                    variant="outlined"
                    color={SEVERITY_COLOR[item.severity] || 'default'}
                    label={item.severity}
                  />
                </Stack>
                {item.body && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                    {item.body}
                  </Typography>
                )}
                {/* The evidence. A notice you cannot check is one you learn to skim. */}
                {item.evidence?.quote && (
                  <Typography
                    variant="body2"
                    sx={{ mt: 0.5, pl: 1, borderLeft: '3px solid rgba(0,0,0,0.12)', fontStyle: 'italic' }}
                  >
                    “{item.evidence.quote}”
                  </Typography>
                )}
              </Box>
              {/* Hold the controls on one line. Once the crisis banner
                  collapsed, the dashboard got wider content and these labels
                  started wrapping mid-phrase ("IN / CONTEXT"), which reads as
                  two broken controls rather than one. */}
              <Stack
                direction="row"
                spacing={0.5}
                sx={{ flexShrink: 0, '& button': { whiteSpace: 'nowrap' } }}
              >
                {/* Straight to the turn that caused the notice. Only offered
                    when the producer actually captured a message id — a link
                    that lands on the top of a long transcript is worse than no
                    link, because it looks like it worked. */}
                {item.subject?.conversationId && item.subject?.messageId && onOpenMoment && (
                  <Tooltip title="Open the conversation at this moment">
                    <Button
                      size="small"
                      aria-label="Open the conversation at this moment"
                      onClick={() => onOpenMoment({
                        clientId: clientIdOf(item),
                        // Carried so the detail header can show who this is
                        // before the full client record has loaded.
                        clientName: nameOf(item),
                        conversationId: item.subject.conversationId,
                        messageId: item.subject.messageId
                      })}
                    >
                      In context
                    </Button>
                  </Tooltip>
                )}
                {clientIdOf(item) && onOpenClient && (
                  <Tooltip title={`Open ${nameOf(item) || 'client'}`}>
                    <Button
                      size="small"
                      startIcon={<VisibilityIcon fontSize="small" />}
                      aria-label={`Open ${nameOf(item) || 'client'}`}
                      onClick={() => onOpenClient(clientIdOf(item))}
                    >
                      Open
                    </Button>
                  </Tooltip>
                )}
                {item.state === 'needs_action' && (
                  <Button size="small" disabled={busyId === item._id} onClick={() => move(item._id, 'seen')}>
                    Mark read
                  </Button>
                )}
                <Tooltip title="Done">
                  <span>
                    <IconButton
                      size="small"
                      aria-label="Mark done"
                      disabled={busyId === item._id}
                      onClick={() => move(item._id, 'done')}
                    >
                      <DoneIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Stack>
            </Stack>
          </Box>
        ))}
      </Stack>
    </Paper>
  );
};

export default InboxWidget;
